import React, { useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { AGENT_CLASSES } from '../lib/constants'
import { T, card, tbl, th, td, btnPrimary, btnSecondary, alert as ALT } from '../lib/theme'
import * as XLSX from 'xlsx'

// ── Style maps ───────────────────────────────────────────────────────────────
const STATUS_OPTS = ['Active', 'Suspended', 'Banned']
const CLASS_OPTS  = ['1', '2', '3']

const STATUS_STYLE = {
  Active:    { c:'#3fb950', bg:'rgba(63,185,80,.12)',  border:'rgba(63,185,80,.25)'  },
  Suspended: { c:'#d29922', bg:'rgba(210,153,34,.12)', border:'rgba(210,153,34,.25)' },
  Banned:    { c:'#f85149', bg:'rgba(248,81,73,.12)',  border:'rgba(248,81,73,.25)'  },
}
const CLASS_STYLE = {
  '1': { c:'#1E90FF', bg:'rgba(30,144,255,.12)', border:'rgba(30,144,255,.25)' },
  '2': { c:'#d29922', bg:'rgba(210,153,34,.12)', border:'rgba(210,153,34,.25)' },
  '3': { c:'#8b949e', bg:'rgba(139,148,158,.12)',border:'rgba(139,148,158,.25)' },
}

const inp  = { width:'100%', padding:'9px 12px', background:T.surface3,
               border:`1px solid ${T.border}`, borderRadius:6, fontSize:12,
               fontFamily:T.font, color:T.txtPrimary, outline:'none' }
const selS = { ...inp, cursor:'pointer' }
const lbl  = { fontSize:10, fontWeight:600, color:T.txtMuted, letterSpacing:'.06em',
               textTransform:'uppercase', display:'block', marginBottom:4 }
const frow = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }

// ── Inline editable pill ─────────────────────────────────────────────────────
function InlinePill({ value, options, styleMap, labelMap, onSave }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])
  const s = styleMap[value] || styleMap[options[0]]
  return (
    <div ref={ref} style={{ position:'relative', display:'inline-block' }}>
      <span onClick={() => setOpen(o=>!o)} title="Click to edit" style={{
        display:'inline-block', padding:'3px 10px', borderRadius:20,
        fontSize:10, fontWeight:700, letterSpacing:'.04em',
        background:s.bg, color:s.c, border:`1px solid ${s.border}`,
        cursor:'pointer', userSelect:'none',
      }}>
        {labelMap ? labelMap[value] : value} ✎
      </span>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, zIndex:200,
          background:T.surface, border:`1px solid ${T.border}`, borderRadius:8,
          boxShadow:'0 8px 24px rgba(0,0,0,.4)', minWidth:160, overflow:'hidden' }}>
          {options.map(opt => {
            const os = styleMap[opt] || styleMap[options[0]]
            return (
              <div key={opt} onClick={() => { onSave(opt); setOpen(false) }} style={{
                padding:'9px 14px', fontSize:12, cursor:'pointer',
                color: opt===value ? os.c : T.txtSecondary,
                background: opt===value ? os.bg : 'transparent',
                fontWeight: opt===value ? 700 : 400,
                display:'flex', alignItems:'center', gap:8,
                borderBottom:`1px solid ${T.border}`,
              }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:os.c, flexShrink:0 }} />
                {labelMap ? labelMap[opt] : opt}
                {opt===value && <span style={{ marginLeft:'auto', fontSize:10 }}>✓</span>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Add Agent Modal ──────────────────────────────────────────────────────────
function AddAgentModal({ onClose, onSave, superAgents, lgas }) {
  const [form, setForm] = useState({ agent_id:'', full_name:'', phone:'', lga_id:'', super_agent_id:'', agent_class:'1', status:'Active' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  function set(k,v) { setForm(f=>({...f,[k]:v})) }
  async function save() {
    if (!form.full_name||!form.lga_id) { setErr('Name and LGA are required.'); return }
    setSaving(true); setErr('')
    const { error } = await supabase.from('agents').insert([{ ...form, super_agent_id:form.super_agent_id||null }])
    if (error) { setErr(error.message); setSaving(false); return }
    onSave()
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14,
        padding:28, width:'100%', maxWidth:520, maxHeight:'88vh', overflowY:'auto',
        boxShadow:'0 24px 64px rgba(0,0,0,.6)' }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.txtPrimary, marginBottom:20 }}>Add Agent</div>
        {err && <div style={ALT.err}>{err}</div>}
        <div style={frow}>
          <div><label style={lbl}>Agent ID</label><input style={inp} value={form.agent_id} onChange={e=>set('agent_id',e.target.value)} placeholder="e.g. AGT-001" /></div>
          <div><label style={lbl}>Full Name *</label><input style={inp} value={form.full_name} onChange={e=>set('full_name',e.target.value)} placeholder="Agent full name" /></div>
        </div>
        <div style={frow}>
          <div><label style={lbl}>Phone</label><input style={inp} value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="08XXXXXXXXX" /></div>
          <div><label style={lbl}>LGA *</label>
            <select style={selS} value={form.lga_id} onChange={e=>set('lga_id',e.target.value)}>
              <option value="">-- Select LGA --</option>
              {lgas.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
            </select></div>
        </div>
        <div style={frow}>
          <div><label style={lbl}>Super Agent</label>
            <select style={selS} value={form.super_agent_id} onChange={e=>set('super_agent_id',e.target.value)}>
              <option value="">-- None --</option>
              {superAgents.map(sa=><option key={sa.id} value={sa.id}>{sa.company_name}</option>)}
            </select></div>
          <div><label style={lbl}>Class</label>
            <select style={selS} value={form.agent_class} onChange={e=>set('agent_class',e.target.value)}>
              {AGENT_CLASSES.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
            </select></div>
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving?'Saving…':'Save Agent'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Add Super Agent Modal ────────────────────────────────────────────────────
function AddSuperAgentModal({ onClose, onSave, lgas }) {
  const [form, setForm] = useState({ owner_name:'', agent_id:'', phone:'', company_name:'', lga_id:'', agent_class:'' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  function set(k,v) { setForm(f=>({...f,[k]:v})) }
  async function save() {
    if (!form.company_name) { setErr('Company Name is required.'); return }
    setSaving(true); setErr('')
    const { error } = await supabase.from('super_agents').insert([{
      owner_name:   form.owner_name||null,
      agent_id:     form.agent_id||null,
      phone:        form.phone||null,
      company_name: form.company_name,
      lga_id:       form.lga_id||null,
      agent_class:  form.agent_class||null,
      status:       'Active',
    }])
    if (error) { setErr(error.message); setSaving(false); return }
    onSave()
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14,
        padding:28, width:'100%', maxWidth:520, maxHeight:'88vh', overflowY:'auto',
        boxShadow:'0 24px 64px rgba(0,0,0,.6)' }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.txtPrimary, marginBottom:4 }}>Add Super Agent</div>
        <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:20 }}>Fields: Name, Agent ID, Phone, Company, LGA, Class</div>
        {err && <div style={ALT.err}>{err}</div>}
        <div style={frow}>
          <div><label style={lbl}>Super Agent Name</label><input style={inp} value={form.owner_name} onChange={e=>set('owner_name',e.target.value)} placeholder="Full name" /></div>
          <div><label style={lbl}>Agent ID</label><input style={inp} value={form.agent_id} onChange={e=>set('agent_id',e.target.value)} placeholder="e.g. SA-001" /></div>
        </div>
        <div style={frow}>
          <div><label style={lbl}>Phone</label><input style={inp} value={form.phone} onChange={e=>set('phone',e.target.value)} placeholder="08XXXXXXXXX" /></div>
          <div><label style={lbl}>Company Name *</label><input style={inp} value={form.company_name} onChange={e=>set('company_name',e.target.value)} placeholder="Registered company name" /></div>
        </div>
        <div style={frow}>
          <div>
            <label style={lbl}>LGA</label>
            <select style={selS} value={form.lga_id} onChange={e=>set('lga_id',e.target.value)}>
              <option value="">-- Select LGA --</option>
              {lgas.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Class</label>
            <select style={selS} value={form.agent_class} onChange={e=>set('agent_class',e.target.value)}>
              <option value="">-- Select Class --</option>
              {CLASS_OPTS.map(c=><option key={c} value={c}>Class {c}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving?'Saving…':'Save Super Agent'}</button>
        </div>
      </div>
    </div>
  )
}

// ── Bulk Import Agents Modal ─────────────────────────────────────────────────
function BulkAgentModal({ onClose, onSave, lgas, superAgents }) {
  const [rows, setRows] = useState([])
  const [errors, setErrors] = useState([])
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [msg, setMsg] = useState(null)
  const [fileInfo, setFileInfo] = useState('')

  const lgaMap = {}; lgas.forEach(l => { lgaMap[l.name.toLowerCase()] = l.id })
  // Build SA lookup by both company_name AND owner_name so either matches
  const saMap  = {}
  superAgents.forEach(sa => {
    if (sa.company_name) saMap[sa.company_name.toLowerCase().trim()] = sa.id
    if (sa.owner_name)   saMap[sa.owner_name.toLowerCase().trim()]   = sa.id
    // Also map agent_id of super agent in case that's used as the reference
    if (sa.agent_id)     saMap[sa.agent_id.toLowerCase().trim()]     = sa.id
  })

  function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    setMsg(null); setRows([]); setErrors([])
    setFileInfo(`Reading: ${file.name} (${(file.size/1024).toFixed(1)} KB)`)
    const reader = new FileReader()
    reader.onerror = () => setMsg({ type:'err', text: 'FileReader error: ' + reader.error })
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result)
        const wb   = XLSX.read(data, { type: 'array' })
        if (!wb.SheetNames.length) { setMsg({ type:'err', text:'No sheets found in file.' }); return }
        const ws   = wb.Sheets[wb.SheetNames[0]]
        const json = XLSX.utils.sheet_to_json(ws, { defval: '' })
        // Normalise keys — strip leading/trailing spaces from all column headers
        const normJson = json.map(row => {
          const r = {}
          Object.keys(row).forEach(k => { r[k.trim()] = row[k] })
          return r
        })
        const detectedCols = Object.keys(normJson[0] || {})
        setFileInfo(`File: ${file.name} — ${normJson.length} rows · Columns: ${detectedCols.join(', ')}`)
        if (!normJson.length) { setMsg({ type:'err', text:'No data rows found. Make sure the file has a header row.' }); return }

        function col(row, ...candidates) {
          for (const k of Object.keys(row)) {
            const kl = k.toLowerCase().trim()
            for (const c of candidates) {
              if (kl === c.toLowerCase() || kl.includes(c.toLowerCase())) return String(row[k]||'').trim()
            }
          }
          return ''
        }

        const errs = []; const parsed = []
        normJson.forEach((row, i) => {
          const name = col(row, 'agent name', 'full name', 'full_name', 'agent_name', 'name')
          const aid  = col(row, 'agent id', 'agent_id', 'agentid', 'id')
          const ph   = col(row, 'phone no', 'phone', 'phone number', 'mobile')
          const sa   = col(row, 'super-agent name', 'super agent name', 'super agent', 'super_agent')
          const co   = col(row, 'company', 'company name', 'company_name', 'firm')
          const lga  = col(row, 'lga', 'local govt', 'local government')
          if (!name) { errs.push(`Row ${i+2}: No agent name found — skipped`); return }
          const lgaId = lga ? lgaMap[lga.toLowerCase()] : null
          // Try to match super agent: exact match first, then partial match
          let saId = null
          const saLookups = [sa, co].filter(Boolean).map(s => s.toLowerCase().trim())
          for (const lookup of saLookups) {
            if (saMap[lookup]) { saId = saMap[lookup]; break }
            // Partial match — find any saMap key that contains the lookup or vice versa
            const partialKey = Object.keys(saMap).find(k => k.includes(lookup) || lookup.includes(k))
            if (partialKey) { saId = saMap[partialKey]; break }
          }
          if (lga && !lgaId) errs.push(`Row ${i+2}: LGA "${lga}" not matched — left blank`)
          if ((sa||co) && !saId) errs.push(`Row ${i+2}: Super Agent "${sa||co}" not matched — will show as unlinked`)
          parsed.push({
            agent_id:      aid   || null,
            full_name:     name,
            phone:         ph    || null,
            lga_id:        lgaId || null,
            super_agent_id:saId  || null,
            agent_class:   '1',
            status:        'Active'
          })
        })
        setRows(parsed); setErrors(errs)
        if (!parsed.length) {
          setMsg({ type:'err', text: `0 rows parsed. Detected columns: ${detectedCols.join(', ')}. Expected: Agent Name, Agent ID, Phone No, Super-Agent Name, Company, LGA` })
        }
      } catch(err) {
        setMsg({ type:'err', text: 'Parse error: ' + err.message })
      }
    }
    reader.readAsArrayBuffer(file)
  }

  async function doImport() {
    setSaving(true); setMsg(null)
    // Deduplicate within the file by agent_id — keep last occurrence
    const seen = new Map()
    rows.forEach(r => {
      const key = r.agent_id || ('__noid__' + Math.random())
      seen.set(key, r)
    })
    const deduped = Array.from(seen.values())
    const dupeCount = rows.length - deduped.length

    const withId    = deduped.filter(r => r.agent_id)
    const withoutId = deduped.filter(r => !r.agent_id)
    let ok = 0, fail = 0, failMsg = ''

    // Upsert one row at a time to avoid batch duplicate key conflicts
    for (const row of withId) {
      const { error } = await supabase.from('agents')
        .upsert([row], { onConflict: 'agent_id' })
      if (error) { fail++; failMsg = error.message }
      else ok++
    }
    for (let i = 0; i < withoutId.length; i += 50) {
      const { error } = await supabase.from('agents').insert(withoutId.slice(i, i+50))
      if (error) { fail += Math.min(50, withoutId.length-i); failMsg = error.message }
      else ok += Math.min(50, withoutId.length-i)
    }
    setSaving(false); setDone(true)
    const dupeNote = dupeCount > 0 ? ` (${dupeCount} duplicate IDs in file removed)` : ''
    setMsg({
      type: fail ? 'err' : 'ok',
      text: fail
        ? `${ok} imported, ${fail} failed — ${failMsg}`
        : `${ok} agents imported. Class 1 / Active assigned.${dupeNote} Safe to re-upload.`
    })
    if (!fail) setTimeout(() => onSave(), 1500)
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14,
        padding:28, width:'100%', maxWidth:560, maxHeight:'88vh', overflowY:'auto',
        boxShadow:'0 24px 64px rgba(0,0,0,.6)' }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.txtPrimary, marginBottom:6 }}>Bulk Import Agents</div>
        <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:16, lineHeight:1.7 }}>
          Columns: <code style={{ background:T.surface3, padding:'2px 6px', borderRadius:4, color:T.blue, fontSize:10 }}>Agent Name · Agent ID · Phone No · Super-Agent Name · Company · LGA</code><br/>
          Auto-assigned: <strong style={{color:T.txtPrimary}}>Class 1</strong> · <strong style={{color:'#3fb950'}}>Active</strong>
        </div>
        <label style={{ border:`2px dashed ${T.border}`, borderRadius:10, padding:'24px',
          textAlign:'center', cursor:'pointer', fontSize:12, color:T.txtSecondary,
          display:'block', background:T.surface3, marginBottom:16 }}>
          📂 Click to select Excel file
          <input type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleFile} />
        </label>
        {fileInfo && <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:8, padding:'6px 10px', background:T.surface3, borderRadius:6 }}>{fileInfo}</div>}
        {rows.length > 0 && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:T.txtPrimary, marginBottom:4 }}>Preview — {rows.length} agents</div>
            <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:8 }}>
              <span style={{color:'#3fb950'}}>✓ {rows.filter(r=>r.super_agent_id).length} linked to Super Agent</span>
              {rows.filter(r=>!r.super_agent_id).length > 0 && <span style={{color:'#f0883e',marginLeft:12}}>⚠ {rows.filter(r=>!r.super_agent_id).length} unlinked — check Super-Agent Name matches company name in system</span>}
            </div>
            {errors.length > 0 && <div style={{ ...ALT.err, marginBottom:10 }}>{errors.slice(0,4).map((e,i)=><div key={i}>{e}</div>)}</div>}
            <div style={{ maxHeight:160, overflowY:'auto', border:`1px solid ${T.border}`, borderRadius:8 }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                <thead><tr>{['ID','Name','LGA','SA'].map(h=><th key={h} style={{ background:T.surface2, color:T.txtSecondary, padding:'6px 10px', textAlign:'left', fontSize:10, borderBottom:`1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
                <tbody>
                  {rows.slice(0,6).map((r,i)=>(
                    <tr key={i}>
                      <td style={{ padding:'5px 10px', borderBottom:`1px solid ${T.border}`, color:T.txtSecondary }}>{r.agent_id||'—'}</td>
                      <td style={{ padding:'5px 10px', borderBottom:`1px solid ${T.border}`, fontWeight:600, color:T.txtPrimary }}>{r.full_name}</td>
                      <td style={{ padding:'5px 10px', borderBottom:`1px solid ${T.border}`, color:r.lga_id?'#3fb950':'#f85149' }}>{r.lga_id?'✓':'✗'}</td>
                      <td style={{ padding:'5px 10px', borderBottom:`1px solid ${T.border}`, color:r.super_agent_id?'#3fb950':'#f0883e', fontSize:10 }}>{r.super_agent_id ? (superAgents.find(sa=>sa.id===r.super_agent_id)?.company_name||'✓') : '✗ unlinked'}</td>
                    </tr>
                  ))}
                  {rows.length>6&&<tr><td colSpan={4} style={{ padding:'5px 10px', color:T.txtMuted, textAlign:'center' }}>…{rows.length-6} more</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {msg && <div style={msg.type==='ok'?ALT.ok:ALT.err}>{msg.text}</div>}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
          <button style={btnSecondary} onClick={onClose}>Close</button>
          {fileInfo&&!done&&<button style={{...btnPrimary, opacity:rows.length===0?.4:1}} onClick={doImport} disabled={saving||rows.length===0}>{saving?'Importing…':rows.length>0?`Import ${rows.length} Agents`:'No valid rows'}</button>}
        </div>
      </div>
    </div>
  )
}

// ── Bulk Import Super Agents Modal ───────────────────────────────────────────
function BulkSuperAgentModal({ onClose, onSave, lgas }) {
  const [rows, setRows] = useState([])
  const [errors, setErrors] = useState([])
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const [msg, setMsg] = useState(null)
  const [fileInfo, setFileInfo] = useState('')
  const lgaMap = {}; lgas.forEach(l => { lgaMap[l.name.toLowerCase()] = l.id })

  function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    setMsg(null); setRows([]); setErrors([])
    setFileInfo(`Reading: ${file.name} (${(file.size/1024).toFixed(1)} KB)`)
    const reader = new FileReader()
    reader.onerror = () => setMsg({ type:'err', text: 'FileReader error: ' + reader.error })
    reader.onload = (evt) => {
      try {
        const wb   = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' })
        if (!wb.SheetNames.length) { setMsg({ type:'err', text:'No sheets found in file.' }); return }
        const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' })
        if (!json.length) { setMsg({ type:'err', text:'No data rows found. Make sure the file has a header row.' }); return }

        // Normalise keys — strip leading/trailing spaces from all column headers
        const normJson = json.map(row => {
          const r = {}
          Object.keys(row).forEach(k => { r[k.trim()] = row[k] })
          return r
        })
        const detectedCols = Object.keys(normJson[0] || {})
        setFileInfo(`File: ${file.name} — ${normJson.length} rows · Columns: ${detectedCols.join(', ')}`)

        // Flexible column finder — case-insensitive, partial match
        function col(row, ...candidates) {
          for (const k of Object.keys(row)) {
            const kl = k.toLowerCase().trim()
            for (const c of candidates) {
              if (kl === c.toLowerCase() || kl.includes(c.toLowerCase())) return String(row[k]||'').trim()
            }
          }
          return ''
        }

        const errs = []; const parsed = []
        normJson.forEach((row, i) => {
          const name    = col(row, 'name', 'super agent name', 'owner_name', 'owner name', 'agent name')
          const aid     = col(row, 'agent id', 'agent_id', 'agentid', 'id')
          const ph      = col(row, 'phone no', 'phone', 'phone number', 'mobile')
          const company = col(row, 'company name', 'company', 'company_name', 'business name', 'firm')
          const lga     = col(row, 'lga', 'local govt', 'local government')
          if (!company && !name) { errs.push(`Row ${i+2}: No Company Name or Name found — skipped`); return }
          const lgaId = lga ? lgaMap[lga.toLowerCase()] : null
          if (lga && !lgaId) errs.push(`Row ${i+2}: LGA "${lga}" not matched — left blank`)
          parsed.push({
            owner_name:   name    || null,
            agent_id:     aid     || null,
            phone:        ph      || null,
            company_name: company || name,  // fallback: use name as company if company blank
            lga_id:       lgaId   || null,
            status:       'Active'
          })
        })
        setRows(parsed); setErrors(errs)
        if (!parsed.length) {
          setMsg({ type:'err', text: `0 rows parsed. Detected columns (after trim): ${detectedCols.join(', ')}. Expected: Name, Agent ID, Phone No, Company Name, LGA` })
        }
      } catch(err) {
        setMsg({ type:'err', text: 'Parse error: ' + err.message })
      }
    }
    reader.readAsArrayBuffer(file)
  }

  async function doImport() {
    setSaving(true); setMsg(null)
    // Deduplicate within the file by agent_id — keep last occurrence
    const seen = new Map()
    rows.forEach(r => {
      const key = r.agent_id || ('__noid__' + Math.random())
      seen.set(key, r)
    })
    const deduped = Array.from(seen.values())
    const dupeCount = rows.length - deduped.length

    const withId    = deduped.filter(r => r.agent_id)
    const withoutId = deduped.filter(r => !r.agent_id)
    let ok = 0, fail = 0, failMsg = ''

    // Upsert one row at a time for withId to avoid batch duplicate conflicts
    for (const row of withId) {
      const { error } = await supabase.from('super_agents')
        .upsert([row], { onConflict: 'agent_id' })
      if (error) { fail++; failMsg = error.message }
      else ok++
    }
    // Insert rows without agent_id in small batches
    for (let i = 0; i < withoutId.length; i += 50) {
      const { error } = await supabase.from('super_agents').insert(withoutId.slice(i, i+50))
      if (error) { fail += Math.min(50, withoutId.length-i); failMsg = error.message }
      else ok += Math.min(50, withoutId.length-i)
    }
    setSaving(false); setDone(true)
    const dupeNote = dupeCount > 0 ? ` (${dupeCount} duplicate IDs in file removed)` : ''
    setMsg({
      type: fail ? 'err' : 'ok',
      text: fail
        ? `${ok} imported, ${fail} failed — ${failMsg}`
        : `${ok} Super Agents imported successfully.${dupeNote} Safe to re-upload — existing records were updated.`
    })
    if (!fail) setTimeout(() => onSave(), 1500)
  }
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:14,
        padding:28, width:'100%', maxWidth:560, maxHeight:'88vh', overflowY:'auto',
        boxShadow:'0 24px 64px rgba(0,0,0,.6)' }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.txtPrimary, marginBottom:6 }}>Bulk Import Super Agents</div>
        <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:16, lineHeight:1.7 }}>
          Columns: <code style={{ background:T.surface3, padding:'2px 6px', borderRadius:4, color:T.amber, fontSize:10 }}>Name · Agent ID · Phone No · Company Name · LGA</code>
        </div>
        <label style={{ border:`2px dashed ${T.border}`, borderRadius:10, padding:'24px',
          textAlign:'center', cursor:'pointer', fontSize:12, color:T.txtSecondary,
          display:'block', background:T.surface3, marginBottom:16 }}>
          📂 Click to select Excel file
          <input type="file" accept=".xlsx,.xls" style={{ display:'none' }} onChange={handleFile} />
        </label>
        {fileInfo && <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:8, padding:'6px 10px', background:T.surface3, borderRadius:6 }}>{fileInfo}</div>}
        {rows.length > 0 && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:12, fontWeight:600, color:T.txtPrimary, marginBottom:8 }}>Preview — {rows.length} Super Agents</div>
            {errors.length > 0 && <div style={{ ...ALT.err, marginBottom:10 }}>{errors.slice(0,4).map((e,i)=><div key={i}>{e}</div>)}</div>}
            <div style={{ maxHeight:160, overflowY:'auto', border:`1px solid ${T.border}`, borderRadius:8 }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                <thead><tr>{['Agent ID','Name','Company','LGA'].map(h=><th key={h} style={{ background:T.surface2, color:T.txtSecondary, padding:'6px 10px', textAlign:'left', fontSize:10, borderBottom:`1px solid ${T.border}` }}>{h}</th>)}</tr></thead>
                <tbody>
                  {rows.slice(0,6).map((r,i)=>(
                    <tr key={i}>
                      <td style={{ padding:'5px 10px', borderBottom:`1px solid ${T.border}`, color:T.txtSecondary }}>{r.agent_id||'—'}</td>
                      <td style={{ padding:'5px 10px', borderBottom:`1px solid ${T.border}`, color:T.txtPrimary }}>{r.owner_name||'—'}</td>
                      <td style={{ padding:'5px 10px', borderBottom:`1px solid ${T.border}`, fontWeight:600, color:T.txtPrimary }}>{r.company_name}</td>
                      <td style={{ padding:'5px 10px', borderBottom:`1px solid ${T.border}`, color:r.lga_id?'#3fb950':'#f85149' }}>{r.lga_id?'✓':'✗'}</td>
                    </tr>
                  ))}
                  {rows.length>6&&<tr><td colSpan={4} style={{ padding:'5px 10px', color:T.txtMuted, textAlign:'center' }}>…{rows.length-6} more</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {msg && <div style={msg.type==='ok'?ALT.ok:ALT.err}>{msg.text}</div>}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
          <button style={btnSecondary} onClick={onClose}>Close</button>
          {fileInfo&&!done&&<button style={{...btnPrimary,background:T.amber,opacity:rows.length===0?.4:1}} onClick={doImport} disabled={saving||rows.length===0}>{saving?'Importing…':rows.length>0?`Import ${rows.length} Super Agents`:'No valid rows'}</button>}
        </div>
      </div>
    </div>
  )
}

// ── Add dropdown ─────────────────────────────────────────────────────────────
function AddDropdown({ onAddAgent, onAddSuperAgent }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button style={{ ...btnPrimary, display:'flex', alignItems:'center', gap:6 }}
        onClick={() => setOpen(o=>!o)}>
        + Add <span style={{ fontSize:10, opacity:.8 }}>▾</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, zIndex:200,
          background:T.surface, border:`1px solid ${T.border}`, borderRadius:10,
          boxShadow:'0 8px 24px rgba(0,0,0,.4)', minWidth:190, overflow:'hidden' }}>
          <div onClick={()=>{setOpen(false);onAddAgent()}} style={{ padding:'11px 16px', fontSize:12,
            cursor:'pointer', color:T.txtPrimary, borderBottom:`1px solid ${T.border}`,
            display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ color:T.blue }}>◉</span> Add Agent
          </div>
          <div onClick={()=>{setOpen(false);onAddSuperAgent()}} style={{ padding:'11px 16px', fontSize:12,
            cursor:'pointer', color:T.txtPrimary, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ color:T.amber }}>◈</span> Add Super Agent
          </div>
        </div>
      )}
    </div>
  )
}

// ── Bulk Import dropdown ─────────────────────────────────────────────────────
function BulkDropdown({ onBulkAgent, onBulkSuperAgent }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])
  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button style={{ ...btnSecondary, display:'flex', alignItems:'center', gap:6 }}
        onClick={() => setOpen(o=>!o)}>
        ⬆ Bulk Import <span style={{ fontSize:10, opacity:.8 }}>▾</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, zIndex:200,
          background:T.surface, border:`1px solid ${T.border}`, borderRadius:10,
          boxShadow:'0 8px 24px rgba(0,0,0,.4)', minWidth:200, overflow:'hidden' }}>
          <div onClick={()=>{setOpen(false);onBulkAgent()}} style={{ padding:'11px 16px', fontSize:12,
            cursor:'pointer', color:T.txtPrimary, borderBottom:`1px solid ${T.border}`,
            display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ color:T.blue }}>◉</span> Import Agents
          </div>
          <div onClick={()=>{setOpen(false);onBulkSuperAgent()}} style={{ padding:'11px 16px', fontSize:12,
            cursor:'pointer', color:T.txtPrimary, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ color:T.amber }}>◈</span> Import Super Agents
          </div>
        </div>
      )}
    </div>
  )
}


// ── Shared helpers ────────────────────────────────────────────────────────────
function Field({ label, value }) {
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, letterSpacing:'.07em',
        textTransform:'uppercase', marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:13, color:T.txtPrimary, fontWeight:500,
        background:T.surface3, border:`1px solid ${T.border}`, borderRadius:6,
        padding:'9px 12px', minHeight:38 }}>{value || <span style={{color:T.txtMuted}}>—</span>}</div>
    </div>
  )
}

function EditField({ label, value, onChange, type='text' }) {
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, letterSpacing:'.07em',
        textTransform:'uppercase', marginBottom:4 }}>{label}</div>
      <input type={type} value={value||''} onChange={e=>onChange(e.target.value)}
        style={{ width:'100%', padding:'9px 12px', background:T.surface3,
          border:`1px solid ${T.blue}`, borderRadius:6, fontSize:12,
          fontFamily:T.font, color:T.txtPrimary, outline:'none' }} />
    </div>
  )
}

function EditSelect({ label, value, options, onChange }) {
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted, letterSpacing:'.07em',
        textTransform:'uppercase', marginBottom:4 }}>{label}</div>
      <select value={value||''} onChange={e=>onChange(e.target.value)}
        style={{ width:'100%', padding:'9px 12px', background:T.surface3,
          border:`1px solid ${T.blue}`, borderRadius:6, fontSize:12,
          fontFamily:T.font, color:T.txtPrimary, outline:'none', cursor:'pointer' }}>
        <option value="">-- Select --</option>
        {options.map(o => <option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
      </select>
    </div>
  )
}

function AuditLog({ logs }) {
  if (!logs.length) return (
    <div style={{ fontSize:11, color:T.txtMuted, fontStyle:'italic' }}>No edits recorded yet.</div>
  )
  return (
    <div style={{ maxHeight:160, overflowY:'auto' }}>
      {logs.map((log, i) => (
        <div key={i} style={{ display:'flex', gap:10, paddingBottom:10,
          borderBottom: i<logs.length-1 ? `1px solid ${T.border}` : 'none', marginBottom:10 }}>
          <div style={{ width:32, height:32, borderRadius:'50%', background:T.blueGlow,
            border:`1px solid rgba(30,144,255,.3)`, display:'flex', alignItems:'center',
            justifyContent:'center', fontSize:12, color:T.blue, flexShrink:0, fontWeight:700 }}>
            {(log.edited_by||'?')[0].toUpperCase()}
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11, fontWeight:600, color:T.txtPrimary }}>{log.edited_by}</div>
            <div style={{ fontSize:10, color:T.txtMuted, marginBottom:4 }}>
              {new Date(log.edited_at).toLocaleString('en-GB',{dateStyle:'medium',timeStyle:'short'})}
            </div>
            {log.changes && Object.entries(log.changes).map(([field, {from, to}]) => (
              <div key={field} style={{ fontSize:10, color:T.txtSecondary, marginBottom:2 }}>
                <span style={{ color:T.txtMuted }}>{field}:</span>{' '}
                <span style={{ color:'#f85149', textDecoration:'line-through' }}>{String(from||'—')}</span>
                {' → '}
                <span style={{ color:'#3fb950' }}>{String(to||'—')}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function CardOverlay({ children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', zIndex:500,
      display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:16,
        width:'100%', maxWidth:600, maxHeight:'90vh', overflowY:'auto',
        boxShadow:'0 32px 80px rgba(0,0,0,.6)', display:'flex', flexDirection:'column' }}>
        {children}
      </div>
    </div>
  )
}

// ── Agent Detail Card ─────────────────────────────────────────────────────────
function AgentCard({ agent, superAgents, lgas, roleCtx, onClose, onSaved }) {
  const admin = roleCtx?.role === 'admin'
  const [editing, setEditing]   = useState(false)
  const [form,    setForm]      = useState({ ...agent })
  const [logs,    setLogs]      = useState([])
  const [saving,  setSaving]    = useState(false)
  const [msg,     setMsg]       = useState(null)

  useEffect(() => {
    supabase.from('agent_edit_log').select('*')
      .eq('record_id', agent.id).eq('record_type','agent')
      .order('edited_at',{ascending:false}).limit(20)
      .then(({ data }) => setLogs(data||[]))
  }, [agent.id])

  function set(k,v) { setForm(f=>({...f,[k]:v})) }

  async function save() {
    setSaving(true); setMsg(null)
    // Build diff
    const changes = {}
    const fields = ['agent_id','full_name','phone','agent_class','status','lga_id','super_agent_id']
    fields.forEach(f => {
      if (String(form[f]||'') !== String(agent[f]||'')) {
        changes[f] = { from: agent[f], to: form[f] }
      }
    })
    const { error } = await supabase.from('agents').update({
      agent_id:       form.agent_id       || null,
      full_name:      form.full_name,
      phone:          form.phone          || null,
      agent_class:    form.agent_class,
      status:         form.status,
      lga_id:         form.lga_id         || null,
      super_agent_id: form.super_agent_id || null,
    }).eq('id', agent.id)
    if (error) { setMsg({ type:'err', text:error.message }); setSaving(false); return }
    // ── Critical path done — unblock the UI immediately ──
    onSaved(form)
    setEditing(false)
    setSaving(false)
    setMsg({ type:'ok', text:'Agent updated.' })
    // ── Background: audit log (non-blocking) ──
    if (Object.keys(changes).length) {
      supabase.from('agent_edit_log').insert([{
        record_id:   agent.id,
        record_type: 'agent',
        edited_by:   'admin',
        changes,
      }]).then(({ error: logErr }) => {
        if (!logErr) {
          supabase.from('agent_edit_log').select('*')
            .eq('record_id', agent.id).eq('record_type','agent')
            .order('edited_at',{ascending:false}).limit(20)
            .then(({ data }) => { if (data) setLogs(data) })
        }
      })
    }
  }

  const saName = superAgents.find(s=>s.id===agent.super_agent_id)?.company_name
  const lgaName = lgas.find(l=>l.id===agent.lga_id)?.name
  const STATUS_S = STATUS_STYLE[agent.status] || STATUS_STYLE.Active

  return (
    <CardOverlay>
      {/* Header */}
      <div style={{ padding:'20px 24px 16px', borderBottom:`1px solid ${T.border}`,
        display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:48, height:48, borderRadius:12,
            background:`linear-gradient(135deg,${T.blue},#0a5ca8)`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:20, fontWeight:900, color:'#fff', flexShrink:0 }}>
            {(agent.full_name||'A')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:T.txtPrimary }}>{agent.full_name}</div>
            <div style={{ fontSize:11, color:T.txtSecondary, marginTop:2 }}>
              Agent ID: <span style={{ color:T.blue, fontFamily:'monospace' }}>{agent.agent_id||'—'}</span>
              {' · '}
              <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700,
                background:STATUS_S.bg, color:STATUS_S.c, border:`1px solid ${STATUS_S.border}` }}>
                {agent.status}
              </span>
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {admin && !editing && (
            <button onClick={()=>setEditing(true)} style={{ padding:'7px 16px',
              background:T.blueGlow, color:T.blue, border:`1px solid rgba(30,144,255,.3)`,
              borderRadius:6, fontSize:12, fontWeight:700, fontFamily:T.font, cursor:'pointer' }}>
              ✎ Edit
            </button>
          )}
          {editing && (
            <>
              <button onClick={()=>{ setEditing(false); setForm({...agent}); setMsg(null) }}
                style={{ padding:'7px 14px', background:T.surface3, color:T.txtSecondary,
                  border:`1px solid ${T.border}`, borderRadius:6, fontSize:12,
                  fontFamily:T.font, cursor:'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving}
                style={{ padding:'7px 16px', background:T.blue, color:'#fff',
                  border:'none', borderRadius:6, fontSize:12, fontWeight:700,
                  fontFamily:T.font, cursor:'pointer' }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          )}
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:'50%',
            background:T.surface3, border:`1px solid ${T.border}`, color:T.txtSecondary,
            fontSize:16, cursor:'pointer', display:'flex', alignItems:'center',
            justifyContent:'center', fontFamily:T.font }}>×</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding:24, flex:1 }}>
        {msg && <div style={msg.type==='ok'?ALT.ok:ALT.err}>{msg.text}</div>}

        {!admin && (
          <div style={{ ...ALT.info, marginBottom:16, fontSize:11 }}>
            👁 Read-only view — contact an administrator to make changes
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
          {editing ? <>
            <div style={{ paddingRight:12 }}>
              <EditField label="Agent ID"   value={form.agent_id}   onChange={v=>set('agent_id',v)} />
              <EditField label="Full Name"  value={form.full_name}  onChange={v=>set('full_name',v)} />
              <EditField label="Phone"      value={form.phone}      onChange={v=>set('phone',v)} />
            </div>
            <div style={{ paddingLeft:12 }}>
              <EditSelect label="Agent Class" value={form.agent_class}
                options={[{value:'1',label:'Class 1'},{value:'2',label:'Class 2'},{value:'3',label:'Class 3'}]}
                onChange={v=>set('agent_class',v)} />
              <EditSelect label="Status" value={form.status}
                options={STATUS_OPTS} onChange={v=>set('status',v)} />
              <EditSelect label="LGA" value={form.lga_id}
                options={lgas.map(l=>({value:l.id,label:l.name}))} onChange={v=>set('lga_id',v)} />
              <EditSelect label="Super Agent" value={form.super_agent_id||''}
                options={[{value:'',label:'None / Independent'},...superAgents.map(sa=>({value:sa.id,label:sa.company_name}))]}
                onChange={v=>set('super_agent_id',v||null)} />
            </div>
          </> : <>
            <div style={{ paddingRight:12 }}>
              <Field label="Full Name"   value={agent.full_name} />
              <Field label="Phone"       value={agent.phone} />
              <Field label="LGA"         value={lgaName} />
            </div>
            <div style={{ paddingLeft:12 }}>
              <Field label="Agent Class" value={`Class ${agent.agent_class}`} />
              <Field label="Super Agent" value={saName || 'Independent'} />
              <Field label="Created"     value={agent.created_at ? new Date(agent.created_at).toLocaleDateString('en-GB') : '—'} />
            </div>
          </>}
        </div>

        {/* Audit log — admin only */}
        {admin && (
          <div style={{ marginTop:8, paddingTop:16, borderTop:`1px solid ${T.border}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.txtMuted, textTransform:'uppercase',
              letterSpacing:'.07em', marginBottom:12 }}>Edit History</div>
            <AuditLog logs={logs} />
          </div>
        )}
      </div>
    </CardOverlay>
  )
}

// ── Super Agent Detail Card ───────────────────────────────────────────────────
function SuperAgentCard({ sa, lgas, agentCount, roleCtx, onClose, onSaved }) {
  const admin = roleCtx?.role === 'admin'
  const [editing, setEditing] = useState(false)
  const [form,    setForm]    = useState({ ...sa })
  const [logs,    setLogs]    = useState([])
  const [saving,  setSaving]  = useState(false)
  const [msg,     setMsg]     = useState(null)

  useEffect(() => {
    supabase.from('agent_edit_log').select('*')
      .eq('record_id', sa.id).eq('record_type','super_agent')
      .order('edited_at',{ascending:false}).limit(20)
      .then(({ data }) => setLogs(data||[]))
  }, [sa.id])

  function set(k,v) { setForm(f=>({...f,[k]:v})) }

  async function save() {
    setSaving(true); setMsg(null)
    const changes = {}
    const fields = ['agent_id','owner_name','phone','company_name','lga_id','agent_class','status','status_reason']
    fields.forEach(f => {
      if (String(form[f]||'') !== String(sa[f]||'')) {
        changes[f] = { from: sa[f], to: form[f] }
      }
    })
    const { error } = await supabase.from('super_agents').update({
      agent_id:      form.agent_id      || null,
      owner_name:    form.owner_name    || null,
      phone:         form.phone         || null,
      company_name:  form.company_name,
      lga_id:        form.lga_id        || null,
      agent_class:   form.agent_class   || null,
      status:        form.status,
      status_reason: form.status_reason || null,
    }).eq('id', sa.id)
    if (error) { setMsg({ type:'err', text:error.message }); setSaving(false); return }
    // ── Critical path done — unblock the UI immediately ──
    onSaved(form)
    setEditing(false)
    setSaving(false)
    setMsg({ type:'ok', text:'Super Agent updated.' })
    // ── Background: cascade class + audit log (non-blocking) ──
    if (form.agent_class && form.agent_class !== sa.agent_class) {
      supabase.from('agents').update({ agent_class: form.agent_class }).eq('super_agent_id', sa.id)
        .then(() => {})
    }
    if (Object.keys(changes).length) {
      supabase.from('agent_edit_log').insert([{
        record_id:   sa.id,
        record_type: 'super_agent',
        edited_by:   'admin',
        changes,
      }]).then(({ error: logErr }) => {
        if (!logErr) {
          supabase.from('agent_edit_log').select('*')
            .eq('record_id', sa.id).eq('record_type','super_agent')
            .order('edited_at',{ascending:false}).limit(20)
            .then(({ data }) => { if (data) setLogs(data) })
        }
      })
    }
  }

  const lgaName = lgas.find(l=>l.id===sa.lga_id)?.name
  const STATUS_S = STATUS_STYLE[sa.status] || STATUS_STYLE.Active

  return (
    <CardOverlay>
      {/* Header */}
      <div style={{ padding:'20px 24px 16px', borderBottom:`1px solid ${T.border}`,
        display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:48, height:48, borderRadius:12,
            background:`linear-gradient(135deg,${T.amber},#c07d0a)`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:20, fontWeight:900, color:'#fff', flexShrink:0 }}>
            {(sa.company_name||'S')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:T.txtPrimary }}>{sa.company_name}</div>
            <div style={{ fontSize:11, color:T.txtSecondary, marginTop:2 }}>
              {sa.agent_id && <span>Agent ID: <span style={{ color:T.amber, fontFamily:'monospace' }}>{sa.agent_id}</span> · </span>}
              <span style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700,
                background:STATUS_S.bg, color:STATUS_S.c, border:`1px solid ${STATUS_S.border}` }}>
                {sa.status}
              </span>
              <span style={{ marginLeft:8, color:T.txtMuted, fontSize:10 }}>
                {agentCount} agent{agentCount!==1?'s':''} linked
              </span>
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {admin && !editing && (
            <button onClick={()=>setEditing(true)} style={{ padding:'7px 16px',
              background:'rgba(245,166,35,.1)', color:T.amber,
              border:`1px solid rgba(245,166,35,.3)`, borderRadius:6,
              fontSize:12, fontWeight:700, fontFamily:T.font, cursor:'pointer' }}>
              ✎ Edit
            </button>
          )}
          {editing && (
            <>
              <button onClick={()=>{ setEditing(false); setForm({...sa}); setMsg(null) }}
                style={{ padding:'7px 14px', background:T.surface3, color:T.txtSecondary,
                  border:`1px solid ${T.border}`, borderRadius:6, fontSize:12,
                  fontFamily:T.font, cursor:'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving}
                style={{ padding:'7px 16px', background:T.amber, color:'#fff',
                  border:'none', borderRadius:6, fontSize:12, fontWeight:700,
                  fontFamily:T.font, cursor:'pointer' }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          )}
          <button onClick={onClose} style={{ width:30, height:30, borderRadius:'50%',
            background:T.surface3, border:`1px solid ${T.border}`, color:T.txtSecondary,
            fontSize:16, cursor:'pointer', display:'flex', alignItems:'center',
            justifyContent:'center', fontFamily:T.font }}>×</button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding:24, flex:1 }}>
        {msg && <div style={msg.type==='ok'?ALT.ok:ALT.err}>{msg.text}</div>}

        {!admin && (
          <div style={{ ...ALT.info, marginBottom:16, fontSize:11 }}>
            👁 Read-only view — contact an administrator to make changes
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
          {editing ? <>
            <div style={{ paddingRight:12 }}>
              <EditField label="Company Name" value={form.company_name} onChange={v=>set('company_name',v)} />
              <EditField label="Owner Name"   value={form.owner_name}   onChange={v=>set('owner_name',v)} />
              <EditField label="Agent ID"     value={form.agent_id}     onChange={v=>set('agent_id',v)} />
            </div>
            <div style={{ paddingLeft:12 }}>
              <EditField label="Phone" value={form.phone} onChange={v=>set('phone',v)} />
              <EditSelect label="LGA"    value={form.lga_id}
                options={lgas.map(l=>({value:l.id,label:l.name}))} onChange={v=>set('lga_id',v)} />
              <EditSelect label="Status" value={form.status}
                options={STATUS_OPTS} onChange={v=>set('status',v)} />
              <EditSelect label="Class" value={form.agent_class||''}
                options={[{value:'',label:'— No Class —'},...CLASS_OPTS.map(c=>({value:c,label:`Class ${c}`}))]}
                onChange={v=>set('agent_class',v)} />
            </div>
          </> : <>
            <div style={{ paddingRight:12 }}>
              <Field label="Company Name" value={sa.company_name} />
              <Field label="Owner Name"   value={sa.owner_name} />
              <Field label="Phone"        value={sa.phone} />
            </div>
            <div style={{ paddingLeft:12 }}>
              <Field label="Agent ID" value={sa.agent_id} />
              <Field label="LGA"      value={lgaName} />
              <Field label="Class"    value={sa.agent_class ? `Class ${sa.agent_class}` : '—'} />
              <Field label="Agents Linked" value={agentCount + ' agents'} />
            </div>
          </>}
          {/* Ban Reason — show in both view and edit when status is Banned */}
          {(form.status === 'Banned' || sa.status === 'Banned') && (
            <div style={{ gridColumn:'1 / -1', marginTop:8 }}>
              {editing ? (
                <div>
                  <label style={{ fontSize:10, fontWeight:600, color:'#f85149', letterSpacing:'.06em',
                    textTransform:'uppercase', display:'block', marginBottom:4 }}>Ban Reason / Notes</label>
                  <textarea
                    value={form.status_reason||''}
                    onChange={e=>set('status_reason',e.target.value)}
                    rows={3}
                    placeholder="Enter reason for ban…"
                    style={{ width:'100%', padding:'9px 12px', background:T.surface3,
                      border:'1px solid rgba(248,81,73,.4)', borderRadius:6, fontSize:12,
                      fontFamily:T.font, color:T.txtPrimary, outline:'none', resize:'vertical', boxSizing:'border-box' }}
                  />
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:10, fontWeight:600, color:'#f85149', letterSpacing:'.06em',
                    textTransform:'uppercase', marginBottom:4 }}>Ban Reason / Notes</div>
                  <div style={{ fontSize:12, color:T.txtPrimary, background:'rgba(248,81,73,.07)',
                    border:'1px solid rgba(248,81,73,.2)', borderRadius:6, padding:'9px 12px',
                    minHeight:36 }}>
                    {sa.status_reason || <span style={{ color:T.txtMuted, fontStyle:'italic' }}>No reason recorded</span>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Audit log — admin only */}
        {admin && (
          <div style={{ marginTop:8, paddingTop:16, borderTop:`1px solid ${T.border}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:T.txtMuted, textTransform:'uppercase',
              letterSpacing:'.07em', marginBottom:12 }}>Edit History</div>
            <AuditLog logs={logs} />
          </div>
        )}
      </div>
    </CardOverlay>
  )
}

// ── Stable styles (defined outside component to prevent remount on re-render) ─
const selStyle = { fontSize:12, fontFamily:T.font, background:T.surface3,
  border:`1px solid ${T.border}`, borderRadius:6, padding:'8px 12px',
  color:T.txtPrimary, cursor:'pointer' }
const searchStyle = { ...selStyle, minWidth:200, cursor:'text' }

// ── Main Page ────────────────────────────────────────────────────────────────
export default function Agents({ session, roleCtx }) {
  const [agents,       setAgents]       = useState([])
  const [superAgents,  setSuperAgents]  = useState([])
  const [lgas,         setLgas]         = useState([])
  const [loading,      setLoading]      = useState(true)
  const [modal,        setModal]        = useState(null)
  const [viewMode,     setViewMode]     = useState('agents') // 'agents' | 'superagents'
  const [selectedAgent, setSelectedAgent] = useState(null)  // agent card
  const [selectedSA,    setSelectedSA]    = useState(null)  // super agent card
  const [filterLga,    setFilterLga]    = useState('')
  const [filterSA,     setFilterSA]     = useState('')
  const [filterClass,  setFilterClass]  = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [search,       setSearch]       = useState('')

  async function load() {
    const [{ data:ag }, { data:sa }, { data:lg }] = await Promise.all([
      supabase.from('agents').select('*, lgas(name,zone,tier), super_agents(company_name)').order('full_name'),
      supabase.from('super_agents').select('*, lgas(name,zone)').order('company_name'),
      supabase.from('lgas').select('*').order('name'),
    ])
    setAgents(ag||[]); setSuperAgents(sa||[]); setLgas(lg||[])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  // Update a single agent field (optimistic)
  async function updateAgent(id, field, value) {
    setAgents(prev => prev.map(a => a.id===id ? { ...a, [field]:value } : a))
    await supabase.from('agents').update({ [field]:value }).eq('id', id)
  }

  // Update super agent status — and cascade to all child agents
  async function updateSAStatus(saId, status) {
    setSuperAgents(prev => prev.map(sa => sa.id===saId ? { ...sa, status } : sa))
    await supabase.from('super_agents').update({ status }).eq('id', saId)
    // Cascade to child agents only when banning or suspending
    if (status === 'Banned' || status === 'Suspended') {
      const { error: agentErr } = await supabase.from('agents').update({ status }).eq('super_agent_id', saId)
      if (agentErr) console.error('Agent cascade failed:', agentErr)
      setAgents(prev => prev.map(a => a.super_agent_id===saId ? { ...a, status } : a))
    }
    // Auto-close open enforcement cases when banning a super agent
    if (status === 'Banned') {
      supabase.from('enforcement_cases')
        .update({
          status: 'closed',
          closing_note: 'Agent banned — case auto-closed.',
          closed_at: new Date().toISOString(),
        })
        .eq('super_agent_id', saId)
        .in('status', ['open', 'under_review'])
        .then(() => {})
    }
    // When reinstating super agent, reactivate child agents too
    if (status === 'Active') {
      await supabase.from('agents').update({ status:'Active' }).eq('super_agent_id', saId)
      setAgents(prev => prev.map(a => a.super_agent_id===saId ? { ...a, status:'Active' } : a))
    }
  }

  async function updateSAClass(saId, agent_class) {
    setSuperAgents(prev => prev.map(sa => sa.id===saId ? { ...sa, agent_class } : sa))
    await supabase.from('super_agents').update({ agent_class }).eq('id', saId)
    // Cascade class to all linked agents
    await supabase.from('agents').update({ agent_class }).eq('super_agent_id', saId)
    setAgents(prev => prev.map(a => a.super_agent_id===saId ? { ...a, agent_class } : a))
  }

  const q = search.toLowerCase()
  const filteredAgents = agents.filter(a =>
    (!filterLga    || a.lga_id === filterLga) &&
    (!filterSA     || a.super_agent_id === filterSA) &&
    (!filterClass  || a.agent_class === filterClass) &&
    (!filterStatus || a.status === filterStatus) &&
    (!q            || (a.full_name||'').toLowerCase().includes(q) ||
                     String(a.agent_id||'').toLowerCase().includes(q))
  )

  const filteredSA = superAgents.filter(sa =>
    (!filterLga    || sa.lga_id === filterLga) &&
    (!filterStatus || sa.status === filterStatus) &&
    (!q            || (sa.company_name||'').toLowerCase().includes(q) ||
                     (sa.owner_name||'').toLowerCase().includes(q) ||
                     String(sa.agent_id||'').toLowerCase().includes(q))
  )

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading agents…</div>

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>Agents</div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          {agents.length} agents · {superAgents.length} super agents · Click Class or Status to edit inline
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <AddDropdown onAddAgent={()=>setModal('agent')} onAddSuperAgent={()=>setModal('superagent')} />
        <BulkDropdown onBulkAgent={()=>setModal('bulk-agent')} onBulkSuperAgent={()=>setModal('bulk-sa')} />

        {/* View toggle */}
        <div style={{ display:'flex', borderRadius:8, overflow:'hidden', border:`1px solid ${T.border}` }}>
          {[['agents','All Agents'],['superagents','Super Agents']].map(([v,lbl2])=>(
            <button key={v} onClick={()=>{ setViewMode(v); setFilterSA('') }} style={{
              padding:'7px 14px', fontSize:11, fontWeight:700, fontFamily:T.font,
              cursor:'pointer', border:'none', transition:'all .15s',
              background: viewMode===v ? T.blue : T.surface3,
              color:       viewMode===v ? '#fff' : T.txtSecondary,
            }}>{lbl2}</button>
          ))}
        </div>

        <input style={searchStyle} placeholder="Search name or ID…"
          value={search} onChange={e=>setSearch(e.target.value)} />
        <select style={selStyle} value={filterLga} onChange={e=>setFilterLga(e.target.value)}>
          <option value="">All LGAs</option>
          {lgas.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {viewMode==='agents' && (
          <select style={selStyle} value={filterSA} onChange={e=>setFilterSA(e.target.value)}>
            <option value="">All Super Agents</option>
            {superAgents.map(sa=><option key={sa.id} value={sa.id}>{sa.company_name}</option>)}
          </select>
        )}
        {viewMode==='agents' && (
          <select style={selStyle} value={filterClass} onChange={e=>setFilterClass(e.target.value)}>
            <option value="">All Classes</option>
            {['1','2','3'].map(c=><option key={c} value={c}>Class {c}</option>)}
          </select>
        )}
        <select style={selStyle} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUS_OPTS.map(s=><option key={s}>{s}</option>)}
        </select>
        <span style={{ fontSize:11, color:T.txtMuted, marginLeft:'auto' }}>
          {viewMode==='agents' ? filteredAgents.length : filteredSA.length} shown
        </span>
        <button onClick={() => {
          if (viewMode === 'agents') {
            const rows = filteredAgents.map(a => ({
              'Agent ID':    a.agent_id||'',
              'Full Name':   a.full_name,
              'LGA':         a.lgas?.name||'',
              'Zone':        a.lgas?.zone||'',
              'Class':       a.agent_class||'',
              'Super Agent': a.super_agents?.company_name||'',
              'Status':      a.status||'',
            }))
            const ws = XLSX.utils.json_to_sheet(rows)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'Agents')
            XLSX.writeFile(wb, 'Agents_Export.xlsx')
          } else {
            const rows = filteredSA.map(sa => ({
              'Agent ID':     sa.agent_id||'',
              'Company Name': sa.company_name||'',
              'Owner Name':   sa.owner_name||'',
              'Phone':        sa.phone||'',
              'LGA':          sa.lgas?.name||'',
              'Class':        sa.agent_class||'',
              'Status':       sa.status||'',
              'Ban Reason':   sa.status_reason||'',
            }))
            const ws = XLSX.utils.json_to_sheet(rows)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'Super Agents')
            XLSX.writeFile(wb, 'SuperAgents_Export.xlsx')
          }
        }} style={{ padding:'7px 14px', background:'rgba(63,185,80,.1)', color:'#3fb950',
          border:'1px solid rgba(63,185,80,.25)', borderRadius:6, fontSize:11, fontWeight:700,
          fontFamily:T.font, cursor:'pointer', flexShrink:0 }}>
          ⬇ Export Excel
        </button>
      </div>

      {/* ── Agents Table ── */}
      {viewMode==='agents' && (
        <div style={card}>
          <table style={tbl}>
            <thead><tr>
              {['Agent ID','Name','LGA','Zone','Class','Super Agent','Status'].map(h=><th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filteredAgents.map(a=>(
              // eslint-disable-next-line
                <tr key={a.id} onClick={()=>setSelectedAgent(a)} style={{cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background='rgba(30,144,255,.04)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <td style={{ ...td, color:T.txtSecondary, fontFamily:'monospace', fontSize:11 }}>{a.agent_id||'—'}</td>
                  <td style={{ ...td, fontWeight:600 }}>{a.full_name}</td>
                  <td style={td}>{a.lgas?.name}</td>
                  <td style={{ ...td, color:T.txtSecondary }}>{a.lgas?.zone}</td>
                  <td style={td}>
                    <InlinePill value={a.agent_class||'1'} options={CLASS_OPTS} styleMap={CLASS_STYLE}
                      labelMap={{'1':'Class 1','2':'Class 2','3':'Class 3'}}
                      onSave={v=>updateAgent(a.id,'agent_class',v)} />
                  </td>
                  <td style={{ ...td, color:T.txtSecondary }}>{a.super_agents?.company_name||'—'}</td>
                  <td style={td}>
                    <InlinePill value={a.status||'Active'} options={STATUS_OPTS} styleMap={STATUS_STYLE}
                      onSave={v=>updateAgent(a.id,'status',v)} />
                  </td>
                </tr>
              ))}
              {filteredAgents.length===0&&<tr><td colSpan={7} style={{ ...td, textAlign:'center', color:T.txtMuted }}>No agents found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Super Agents Table ── */}
      {viewMode==='superagents' && (
        <div style={card}>
          <div style={{ fontSize:11, color:T.txtSecondary, marginBottom:12, padding:'8px 12px',
            background:'rgba(245,166,35,.08)', border:'1px solid rgba(245,166,35,.2)', borderRadius:8 }}>
            ⚠ Setting a Super Agent to <strong style={{color:'#f85149'}}>Banned</strong> or <strong style={{color:'#d29922'}}>Suspended</strong> will automatically apply the same status to all agents under them.
          </div>
          <table style={tbl}>
            <thead><tr>
              {['Agent ID','Company Name','Owner Name','Phone','LGA','Zone','Class','Status','Agents'].map(h=><th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filteredSA.map(sa=>{
                const childCount = agents.filter(a=>a.super_agent_id===sa.id).length
                return (
                  <tr key={sa.id} onClick={()=>setSelectedSA(sa)} style={{cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background='rgba(245,166,35,.04)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ ...td, color:T.txtSecondary, fontFamily:'monospace', fontSize:11 }}>{sa.agent_id||'—'}</td>
                    <td style={{ ...td, fontWeight:700, color:T.txtPrimary }}>{sa.company_name}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{sa.owner_name||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{sa.phone||'—'}</td>
                    <td style={td}>{sa.lgas?.name||'—'}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>{sa.lgas?.zone||'—'}</td>
                    <td style={td}>
                      <InlinePill value={sa.agent_class||'1'} options={CLASS_OPTS} styleMap={CLASS_STYLE}
                        labelMap={{'1':'Class 1','2':'Class 2','3':'Class 3'}}
                        onSave={v=>updateSAClass(sa.id,v)} />
                    </td>
                    <td style={td}>
                      <InlinePill value={sa.status||'Active'} options={STATUS_OPTS} styleMap={STATUS_STYLE}
                        onSave={v=>updateSAStatus(sa.id,v)} />
                    </td>
                    <td style={{ ...td, color:T.txtSecondary, textAlign:'center' }}>
                      <span style={{ background:T.surface3, border:`1px solid ${T.border}`,
                        borderRadius:20, padding:'2px 10px', fontSize:10, fontWeight:700 }}>
                        {childCount}
                      </span>
                    </td>
                  </tr>
                )
              })}
              {filteredSA.length===0&&<tr><td colSpan={9} style={{ ...td, textAlign:'center', color:T.txtMuted }}>No super agents found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Detail Cards ── */}
      {selectedAgent && (
        <AgentCard
          agent={selectedAgent}
          superAgents={superAgents}
          lgas={lgas}
          roleCtx={roleCtx}
          onClose={()=>setSelectedAgent(null)}
          onSaved={(updated)=>{ setAgents(prev=>prev.map(a=>a.id===updated.id?{...a,...updated}:a)); setSelectedAgent(s=>({...s,...updated})) }}
        />
      )}
      {selectedSA && (
        <SuperAgentCard
          sa={selectedSA}
          lgas={lgas}
          agentCount={agents.filter(a=>a.super_agent_id===selectedSA.id).length}
          roleCtx={roleCtx}
          onClose={()=>setSelectedSA(null)}
          onSaved={(updated)=>{ setSuperAgents(prev=>prev.map(s=>s.id===updated.id?{...s,...updated}:s)); setSelectedSA(s=>({...s,...updated})) }}
        />
      )}
      {modal==='agent'      && <AddAgentModal         onClose={()=>setModal(null)} onSave={()=>{setModal(null);load()}} superAgents={superAgents} lgas={lgas} />}
      {modal==='superagent' && <AddSuperAgentModal     onClose={()=>setModal(null)} onSave={()=>{setModal(null);load()}} lgas={lgas} />}
      {modal==='bulk-agent' && <BulkAgentModal         onClose={()=>setModal(null)} onSave={()=>{setModal(null);load()}} lgas={lgas} superAgents={superAgents} />}
      {modal==='bulk-sa'    && <BulkSuperAgentModal    onClose={()=>setModal(null)} onSave={()=>{setModal(null);load()}} lgas={lgas} />}
    </div>
  )
}
